import React, { useState, createContext, useContext } from 'react';

type ToastType = 'success' | 'error' | 'logic' | 'sync';

interface Toast {
    id: string;
    message: string;
    type: ToastType;
}

interface ToastContextType {
    showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = (message: string, type: ToastType = 'logic') => {
        const id = Math.random().toString(36).substring(2, 9);
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 5000);
    };

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <div className="fixed bottom-20 md:bottom-8 left-4 right-4 md:left-auto md:right-8 z-[200] flex flex-col gap-3 pointer-events-none max-w-md ml-auto">
                {toasts.map(toast => (
                    <div 
                        key={toast.id}
                        className={`pointer-events-auto glass-panel px-5 md:px-6 py-3.5 md:py-4 rounded-2xl shadow-2xl border-l-4 w-full md:min-w-[300px] animate-fade-in transform transition-all duration-500
                            ${toast.type === 'success' ? 'border-green-500 bg-green-500/5' : ''}
                            ${toast.type === 'error' ? 'border-red-500 bg-red-500/5' : ''}
                            ${toast.type === 'logic' ? 'border-azure bg-azure/5' : ''}
                            ${toast.type === 'sync' ? 'border-gold bg-gold/5' : ''}
                        `}
                    >
                        <div className="flex items-start gap-4">
                            <div className={`mt-1 w-2 h-2 rounded-full animate-pulse
                                ${toast.type === 'success' ? 'bg-green-500' : ''}
                                ${toast.type === 'error' ? 'bg-red-500' : ''}
                                ${toast.type === 'logic' ? 'bg-azure' : ''}
                                ${toast.type === 'sync' ? 'bg-gold' : ''}
                            `}></div>
                            <div>
                                <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-500 mb-1 italic">
                                    {toast.type === 'logic' ? 'Logical Handshake' : toast.type === 'sync' ? 'Vault Synchronization' : 'System Notice'}
                                </h4>
                                <p className="text-xs text-white font-medium leading-relaxed">{toast.message}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};

export const useToasts = () => {
    const context = useContext(ToastContext);
    if (!context) throw new Error('useToasts must be used within ToastProvider');
    return context;
};
